import api from '../api';
import type { User } from '../auth';

// User profile data interface
export interface UserProfile extends User {
  bio?: string;
  location?: string;
  website?: string;
  socialLinks?: {
    twitter?: string;
    instagram?: string;
    soundcloud?: string;
    youtube?: string;
    beatport?: string;
    bandcamp?: string;
  };
  genres?: string[];
  skills?: string[];
}

export interface UserPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  darkMode: boolean;
  language: string;
  timezone: string;
}

export interface UserQueryParams {
  limit?: number;
  page?: number;
  sortBy?: string;
}

export interface UserActivityParams {
  limit?: number;
  page?: number;
  type?: string;
}

// User stats interface
export interface UserStats {
  followers: number;
  following: number;
  tracks: number;
  collaborations: number;
  likes: number;
  views: number;
}

// User service for managing user-related operations
const userService = {
  // Get user profile
  getUserProfile: async (userId: string): Promise<UserProfile> => {
    try {
      const response = await api.get<{ profile: UserProfile }>(`users/${userId}/profile`);
      
      if (!response.data.profile) {
        throw new Error('User profile not found');
      }
      
      return response.data.profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (_userId: string, data: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await api.put<{ profile: UserProfile }>('/users/me', data);
    return response.data.profile;
  },

  // Get user preferences
  getUserPreferences: async (): Promise<UserPreferences> => {
    try {
      const response = await api.get<{ preferences: UserPreferences }>('users/preferences');
      return response.data.preferences;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  },

  // Update user preferences
  updatePreferences: async (preferences: Partial<UserPreferences>): Promise<UserPreferences> => {
    const response = await api.put<{ preferences: UserPreferences }>('/users/preferences', preferences);
    return response.data.preferences;
  },

  // Get user stats
  getUserStats: async (userId: string): Promise<UserStats> => {
    try {
      const response = await api.get<{ stats: UserStats }>(`users/${userId}/stats`);
      return response.data.stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  },

  // Follow a user
  followUser: async (userId: string): Promise<void> => {
    await api.post(`/users/${userId}/follow`);
  },

  // Unfollow a user
  unfollowUser: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}/follow`);
  },

  // Get user's followers
  getFollowers: async (userId: string, page = 1, limit = 20): Promise<{ users: UserProfile[]; total: number }> => {
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `/users/${userId}/followers?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Get users followed by a user
  getFollowing: async (userId: string, page = 1, limit = 20): Promise<{ users: UserProfile[]; total: number }> => {
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `/users/${userId}/following?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Search users
  searchUsers: async (query: string, page = 1, limit = 20): Promise<{ users: UserProfile[]; total: number }> => {
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `/users/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Get user's activity feed
  getActivity: async (userId: string, page = 1, limit = 20): Promise<{ activities: any[]; total: number }> => {
    const response = await api.get<{ activities: any[]; total: number }>(
      `/users/${userId}/activity?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Update user's avatar
  updateAvatar: async (userId: string, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await api.post<{ url: string }>(
      `/users/${userId}/avatar`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Delete user's avatar
  deleteAvatar: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}/avatar`);
  },

  // Get popular users
  getPopularUsers: async (params: UserQueryParams = {}): Promise<{ users: UserProfile[], total: number }> => {
    const { limit = 5, page = 1 } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());
    queryParams.append('sortBy', 'followers');
    
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `users/popular?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get new users
  getNewUsers: async (params: UserQueryParams = {}): Promise<{ users: UserProfile[], total: number }> => {
    const { limit = 5, page = 1 } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());
    queryParams.append('sortBy', 'joinedAt');
    
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `users/new?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get active users
  getActiveUsers: async (params: UserQueryParams = {}): Promise<{ users: UserProfile[], total: number }> => {
    const { limit = 5, page = 1 } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());
    queryParams.append('sortBy', 'activity');
    
    const response = await api.get<{ users: UserProfile[]; total: number }>(
      `users/active?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get user activity
  getUserActivity: async (userId: string, params: UserActivityParams = {}): Promise<{ activities: any[], total: number }> => {
    const { limit = 10, page = 1, type } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());
    
    if (type) {
      queryParams.append('type', type);
    }
    
    const response = await api.get<{ activities: any[]; total: number }>(
      `users/${userId}/activity?${queryParams.toString()}`
    );
    return response.data;
  },
};

export default userService; 